// src/hooks/useQuestions.tsx
// Customer questions, backed by the shared in-memory questionsStore (no IndexedDB).
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { QuestionMessage } from '../types/QuestionMessage';
import { MOCK_ORDERS_ENABLED } from '../mocks/config';
import { deleteMessageById } from '../services/orderSync';
import { questionsStore } from '../stores/orderStores';

export function useQuestions(pageSize = 10) {
  const all = useSyncExternalStore(questionsStore.subscribe, questionsStore.getSnapshot);
  const loading = useSyncExternalStore(questionsStore.subscribe, questionsStore.getLoading);
  const [page, setPage] = useState(1);

  // Refetch on mount; concurrent mounts of multiple widgets dedupe to one request.
  useEffect(() => { void questionsStore.refresh(); }, []);

  const sorted = useMemo(
    () => [...all].sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt)),
    [all],
  );
  const total = sorted.length;
  const unread = useMemo(() => sorted.filter((m) => !m.isRead).length, [sorted]);
  const maxPage = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), maxPage);
  const items = useMemo(() => {
    const offset = (safePage - 1) * pageSize;
    return sorted.slice(offset, offset + pageSize);
  }, [sorted, safePage, pageSize]);

  const markRead = useCallback(async (ids: string[]) => {
    questionsStore.markRead(ids ?? []);
  }, []);

  const remove = useCallback(async (ids: string[]) => {
    if (!ids?.length) return;
    if (!MOCK_ORDERS_ENABLED) {
      await Promise.allSettled(ids.map((id) => deleteMessageById(id)));
    }
    questionsStore.removeLocal(ids);
  }, []);

  const add = useCallback(async (m: QuestionMessage) => {
    questionsStore.prepend(m);
    setPage(1);
  }, []);

  return {
    items, unread, total,
    page: safePage, setPage, pageSize, loading,
    reload: () => questionsStore.refresh(),
    markRead, remove, add,
  };
}
